import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenuButton } from '@/components/blocks/evo-sidebar';
import { HelpCircle, Play, RotateCcw, BookOpen, CheckCircle2, FastForward } from 'lucide-react';
import { useTour } from '@/contexts/TourContext';
import { Badge } from '@/components/ui/badge';

export function TourHelpButton() {
  const { isActive, currentStepIndex, startTour, resumeTour, endTour, steps } = useTour();
  const [isOpen, setIsOpen] = useState(false);

  // Has the user progressed past the welcome step (step 0)?
  const hasStarted = currentStepIndex > 0 || isActive;
  // Tour is fully completed when inactive and past the last step
  const isCompleted = !isActive && currentStepIndex >= steps.length - 1 && steps.length > 0;
  // Mid-tour: started but not completed and currently not active (i.e. closed mid-way)
  const isMidTour = !isActive && hasStarted && !isCompleted;

  const completionPercentage = isCompleted
    ? 100
    : steps.length > 0
      ? Math.round((currentStepIndex / steps.length) * 100)
      : 0;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton tooltip="Help & Tour" className="relative font-normal text-teal-600 hover:text-teal-600 [&>svg]:h-4 [&>svg]:w-4">
          <HelpCircle className="h-4 w-4 shrink-0" />
          <span className="group-data-[collapsible=icon]:hidden">Help &amp; Tour</span>
          {/* Step badge — show when tour is active or paused mid-way */}
          {(isActive || isMidTour) && currentStepIndex > 0 && (
            <span className="absolute left-4 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
              {currentStepIndex}
            </span>
          )}
        </SidebarMenuButton>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Product Tour</span>
          {isCompleted && (
            <Badge variant="secondary" className="gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Completed
            </Badge>
          )}
          {isMidTour && (
            <Badge variant="outline" className="gap-1 text-muted-foreground">
              Step {currentStepIndex + 1} of {steps.length}
            </Badge>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Progress bar — show when active or paused mid-tour */}
        {(isActive || isMidTour) && (
          <>
            <div className="px-2 py-2">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-medium">{completionPercentage}%</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>
              {!isActive && (
                <p className="text-xs text-muted-foreground mt-2">
                  Tour paused — pick up where you left off.
                </p>
              )}
            </div>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Resume — only when tour was closed mid-way */}
        {isMidTour && (
          <DropdownMenuItem
            onClick={() => { resumeTour(); setIsOpen(false); }}
            className="gap-2"
          >
            <FastForward className="h-4 w-4" />
            Resume Tour
          </DropdownMenuItem>
        )}

        {/* Exit tour — only while actively running */}
        {isActive && (
          <DropdownMenuItem
            onClick={() => { endTour(); setIsOpen(false); }}
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Exit Tour
          </DropdownMenuItem>
        )}

        {/* Restart — shown when tour has been started (active, paused, or completed) */}
        {hasStarted && (
          <DropdownMenuItem
            onClick={() => { startTour(); setIsOpen(false); }}
            className="gap-2 text-muted-foreground"
          >
            <RotateCcw className="h-4 w-4" />
            Restart from Beginning
          </DropdownMenuItem>
        )}

        {/* Start — only when tour has never been started */}
        {!hasStarted && (
          <DropdownMenuItem
            onClick={() => { startTour(); setIsOpen(false); }}
            className="gap-2"
          >
            <Play className="h-4 w-4" />
            Start Tour
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem className="gap-2" disabled>
          <BookOpen className="h-4 w-4" />
          View Documentation
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
